import { comGenero, metro, type CamadaSpt, type FenomenoSpt, type Vocabulario } from '../../api/spt';

/**
 * A descrição da camada — ondas 357/360 (RN-276, S02–S04, D3, D9).
 *
 * Pedido do Humberto (30/10/2026): «ao clicar para descrever a camada, nada
 * acontece. As classificações, cores etc. devem ser padronizadas (tipo a
 * planilha de Excel) e não com texto livre». A camada abre numa janela (antes
 * o painel nascia ao lado do perfil, fora da tela em monitores menores), e cada
 * campo é uma LISTA do vocabulário da SPTSis, na ordem das colunas da
 * planilha: material, complemento, compacidade/consistência (só a tabela da
 * classe do material, com a sugestão pelo N), plasticidade, cor e segunda cor
 * (362: até duas; «variegada» sozinha, NBR 6484 5.2.7.7) e gênese (siglas da NBR 13441). Nada se digita; a descrição sai
 * montada pelo banco e aparece no alto, ao vivo.
 *
 * Onda 361 (pedido de 30/10/2026: «use a largura total da página para não ter scroll horizontal e conseguirmos fazer
 * a classificação da camada logo ao lado»): a janela virou a coluna da direita da ficha, sempre à vista; a descrição
 * do perfil encolhe para caber. Sem camada escolhida, a coluna diz o que fazer.
 */
export function PainelDaCamada({ camada, numero, vocab, fenomenos, podeUnir, editavel, aoMudar, aoUnir, aoFechar }: {
  camada: CamadaSpt | null; numero: number; vocab: Vocabulario; fenomenos: FenomenoSpt[];
  podeUnir: boolean; editavel: boolean;
  aoMudar: (corpo: Record<string, unknown>) => void; aoUnir: () => void; aoFechar: () => void;
}) {
  if (!camada) {
    return (
      <aside className="cartao spt-painel" aria-label="Classificação da camada">
        <h3>Classificação da camada</h3>
        <p className="sub">Clique na descrição de uma camada, no perfil, para classificá-la aqui. A gênese também se escolhe direto na coluna dela.</p>
      </aside>
    );
  }
  const g = camada.genero;
  const tabela = vocab.estados.filter((e) => e.classe === camada.classe);
  const fen = fenomenos.filter((f) => f.de >= camada.topo && f.de < camada.base);
  const cor = (k: number) => camada.cores[k] ?? '';
  /* 362 · NBR 6484 5.2.7.7: no máximo duas cores; com mais de duas, «variegada», sozinha. */
  const variegada = vocab.cores.find((c) => c.termo.startsWith('VARIEGAD'))?.id;
  const mudarCor = (k: number, v: string) => {
    const nova = v ? Number(v) : null;
    if (nova != null && nova === variegada) { aoMudar({ cores: [nova] }); return; }
    const cores = [0, 1].map((j) => (j === k ? nova : (camada.cores[j] ?? null)))
      .filter((x): x is number => x != null && x !== variegada);
    aoMudar({ cores: [...new Set(cores)] });
  };
  const id = (s: string) => `spt-camada-${s}`;

  return (
    <aside className="cartao spt-painel" aria-label={`Classificação da camada ${numero}`}>
      <div className="spt-painel-topo">
        <h3 className="cresce">Camada {numero} · {metro(camada.topo)} a {metro(camada.base)} m</h3>
        <button type="button" className="botao discreto" aria-label="Fechar a classificação" onClick={aoFechar}>×</button>
      </div>
      <>
        <p className="spt-previa" aria-live="polite">{camada.descricao || 'Escolha o material.'}</p>
        <fieldset disabled={!editavel} className="spt-painel-campos spt-grade-camada">
          <label className="spt-rotulo" htmlFor={id('material')}>Material
            <select id={id('material')} className="campo" value={camada.material ?? ''}
              onChange={(e) => aoMudar({ material: e.target.value || null })}>
              <option value="">Escolha…</option>
              {vocab.materiais.map((m) => <option key={m.codigo} value={m.codigo}>{m.codigo}</option>)}
            </select>
          </label>

          <label className="spt-rotulo" htmlFor={id('complemento')}>Complemento
            <select id={id('complemento')} className="campo" value={camada.complemento ?? ''}
              onChange={(e) => aoMudar({ complemento: e.target.value ? Number(e.target.value) : null })}>
              <option value="">—</option>
              {vocab.complementos.map((c) => <option key={c.id} value={c.id}>{comGenero(c.termo, g)}</option>)}
            </select>
          </label>

          <label className="spt-rotulo" htmlFor={id('estado')}>
            {camada.classe === 1 ? 'Compacidade' : camada.classe === 2 ? 'Consistência' : 'Compacidade / consistência'}
            <select id={id('estado')} className="campo" value={camada.estado ?? ''} disabled={!tabela.length}
              onChange={(e) => aoMudar({ estado: e.target.value || null })}>
              <option value="">{camada.sugestao ? `Sugerido pelo N: ${camada.sugestao}` : tabela.length ? '—' : 'Sem tabela para este material'}</option>
              {tabela.map((e) => <option key={e.termo} value={e.termo}>{comGenero(e.termo, g)}</option>)}
            </select>
          </label>

          <label className="spt-rotulo" htmlFor={id('plasticidade')}>Plasticidade
            <select id={id('plasticidade')} className="campo" value={camada.plasticidade ?? ''}
              onChange={(e) => aoMudar({ plasticidade: e.target.value ? Number(e.target.value) : null })}>
              <option value="">—</option>
              {vocab.plasticidades.map((p) => <option key={p.id} value={p.id}>{comGenero(p.termo, g)}</option>)}
            </select>
          </label>

          {[0, 1].map((k) => (
            <label key={k} className="spt-rotulo" htmlFor={id(`cor${k}`)}>{k === 0 ? 'Cor' : 'Segunda cor'}
              <select id={id(`cor${k}`)} className="campo" value={cor(k)}
                disabled={k > 0 && (!camada.cores[0] || camada.cores[0] === variegada)}
                onChange={(e) => mudarCor(k, e.target.value)}>
                <option value="">—</option>
                {vocab.cores.filter((c) => k === 0 || c.id !== variegada)
                  .map((c) => <option key={c.id} value={c.id}>{comGenero(c.termo, 'F')}</option>)}
              </select>
            </label>
          ))}

          <label className="spt-rotulo" htmlFor={id('genese')}>Gênese (NBR 13441)
            <select id={id('genese')} className="campo" value={camada.genese ?? ''}
              onChange={(e) => aoMudar({ genese: e.target.value || null })}>
              <option value="">Escolha…</option>
              {vocab.geneses.map((x) => (
                <option key={x.sigla} value={x.sigla}>{x.sigla} — {x.nome}{x.fora_da_norma ? ' (fora da norma)' : ''}</option>
              ))}
            </select>
          </label>
        </fieldset>

        <p className="sub">Fenômenos nesta camada: {fen.length ? fen.map((f) => f.texto).join('; ') : 'nenhum'} — lançados no quadro «Fenômenos observados».</p>

        {podeUnir && editavel && (
          <div className="spt-painel-acoes">
            <button type="button" className="botao secundario" onClick={aoUnir}>Unir com a camada de baixo</button>
          </div>
        )}
      </>
    </aside>
  );
}
